import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { BbotButton, BbotModal, Checkbox, PageLoadingSpinner, Select } from "top-component-library";
import { FormRow } from "./styles";

// Mirrors add-integration-dialog.html's MasterSync form + addIntegrationController's
// updateMasterAccountIntegration(): sends the whole fetched-then-edited settings object
// back, not a diff, so local state is seeded directly from the settings prop once loaded.
const MasterSyncDialog = ({ visible, isAdmin, allowedCustomers, settings, onClose, onLoad, onSave }) => {
  const [localSettings, setLocalSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  // Tracks which settings object has already been seeded, so a reopen can't briefly show
  // (and let Submit act on) the hook's still-stale cache from before this open's onLoad()
  // resolves - localSettings is cleared to null on every open and only repopulated once a
  // genuinely new settings reference arrives.
  const consumedSettingsRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setLocalSettings(null);
      onLoad();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (visible && settings && settings !== consumedSettingsRef.current) {
      consumedSettingsRef.current = settings;
      setLocalSettings(settings);
    }
  }, [visible, settings]);

  const setField = (field, value) => setLocalSettings((current) => ({ ...current, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    const succeeded = await onSave(localSettings);
    setSaving(false);
    if (succeeded) onClose();
  };

  return (
    <BbotModal
      title="Sync From Master Account"
      visible={visible}
      onCancel={onClose}
      footer={[
        <BbotButton key="close" onClick={onClose}>
          Close
        </BbotButton>,
        <BbotButton
          key="submit"
          type="primary"
          loading={saving}
          disabled={!localSettings?.source_customer_id}
          onClick={handleSave}
        >
          Submit
        </BbotButton>,
      ]}
    >
      {!localSettings ? (
        <PageLoadingSpinner />
      ) : (
        <div>
          <FormRow>
            <label htmlFor="master-sync-customer">Sync From:</label>
            <Select
              id="master-sync-customer"
              value={localSettings.source_customer_id ?? undefined}
              onChange={(value) => setField("source_customer_id", value)}
            >
              {allowedCustomers.map((customer) => (
                <Select.Option key={customer.customer_id} value={customer.customer_id}>
                  {isAdmin ? customer.name_for_admin : customer.name_for_owner}
                </Select.Option>
              ))}
            </Select>
          </FormRow>
          <FormRow>
            <label>Fields to Sync:</label>
            <Checkbox
              checked={localSettings.sync_menu_items}
              onChange={(event) => setField("sync_menu_items", event.target.checked)}
            >
              Menu items
            </Checkbox>
            <Checkbox
              checked={localSettings.sync_menu_hours}
              onChange={(event) => setField("sync_menu_hours", event.target.checked)}
            >
              Menu hours
            </Checkbox>
            <Checkbox
              checked={localSettings.sync_employees}
              onChange={(event) => setField("sync_employees", event.target.checked)}
            >
              Employees
            </Checkbox>
            <Checkbox
              checked={localSettings.sync_banner_and_icons}
              onChange={(event) => setField("sync_banner_and_icons", event.target.checked)}
            >
              Banner image &amp; icons
            </Checkbox>
            <Checkbox
              checked={localSettings.sync_guest_messages}
              onChange={(event) => setField("sync_guest_messages", event.target.checked)}
            >
              Messages to guests
            </Checkbox>
            <Checkbox
              checked={localSettings.sync_min_order_settings}
              onChange={(event) => setField("sync_min_order_settings", event.target.checked)}
            >
              Minimum order settings
            </Checkbox>
            <Checkbox
              checked={localSettings.sync_promos}
              onChange={(event) => setField("sync_promos", event.target.checked)}
            >
              Promotions
            </Checkbox>
            <Checkbox
              checked={localSettings.sync_delivery_charge}
              onChange={(event) => setField("sync_delivery_charge", event.target.checked)}
            >
              Delivery Charge
            </Checkbox>
          </FormRow>
        </div>
      )}
    </BbotModal>
  );
};

MasterSyncDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  allowedCustomers: PropTypes.arrayOf(
    PropTypes.shape({
      customer_id: PropTypes.string.isRequired,
      name_for_admin: PropTypes.string,
      name_for_owner: PropTypes.string,
    })
  ).isRequired,
  settings: PropTypes.shape({
    source_customer_id: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

MasterSyncDialog.defaultProps = {
  settings: null,
};

export default MasterSyncDialog;
